/* eslint-disable no-param-reassign, arrow-body-style */
// @flow

import * as React from 'react';
import { Query, Mutation } from 'react-apollo';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';
import { CollectionView } from 'wijmo/wijmo';

import type { ColumnsMap, SaveStatus, UserViewState } from '../../types';
import ButtonAction from './ButtonAction';
import ManageViewsModal from './ManageViewsModal';
import SaveAsNewViewModal from './SaveAsNewViewModal';
import { 
  saveNamedViewMutation, 
  createViewMutation,
  setLastUsedViewMutation,
} from '../../graphql/mutations/user';
import { userSavedViewsQuery } from '../../graphql/queries/user/savedViews.query';
import {
  filterColPropertiesToBeSaved,
  parseGroupPanels,
  parseSortDescriptions,
  getCollapsedGroupLevels,
} from '../../utils';
import { DesktopView, MobileView } from '../../config/mediaQuery';

import './SaveViewDropdown.css';

type Props = {
  moduleName: string,
  columnsMapping: ColumnsMap,
  onViewSaved: (SaveStatus, UserViewState, ?string) => void,
  onViewLoaded: (viewName: ?string) => void,
  onResetToDefault: () => void,
  onApplyViewSettings: (viewSettings: Object, viewName: string) => void,
  saveStatus: SaveStatus,
  currentViewName: ?string,
  frozenColumns: number,
  collection: CollectionView,
  filters?: any,
  disabled: boolean,
  getGridFilterDefinition?: () => ?string,
  grid?: any, // FlexGrid instance for getting collapsed state
};

type State = {
  isOpen: boolean,
  showManageModal: boolean,
  showSaveAsModal: boolean,
  saving: boolean,
  creating: boolean,
};

class SaveViewDropdown extends React.Component<Props, State> {
  dropdownRef: { current: null | HTMLDivElement };

  constructor(props: Props) {
    super(props);
    this.state = {
      isOpen: false,
      showManageModal: false,
      showSaveAsModal: false,
      saving: false,
      creating: false,
    };
    this.dropdownRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (event: MouseEvent) => {
    if (this.dropdownRef.current && !this.dropdownRef.current.contains((event.target: any))) {
      this.setState({ isOpen: false });
    }
  };

  /**
   * Get the current view settings to save.
   * This captures all view state including columns, sorting, groups, frozen columns,
   * filters, and collapsed group levels.
   * The filterDefinition is retrieved from the FlexGridFilter component via getGridFilterDefinition
   * to ensure all column filters applied via the UI are captured.
   */
  getViewSettings = () => {
    // Get filter definition from the grid filter component if available,
    // otherwise fall back to the filters prop
    var filterDefinition = null;
    if (this.props.getGridFilterDefinition) {
      filterDefinition = this.props.getGridFilterDefinition();
    }
    // Fall back to filters prop if getGridFilterDefinition is not provided or returns null
    if (!filterDefinition && this.props.filters) {
      filterDefinition = this.props.filters;
    }

    // Capture collapsed group levels from the grid
    var collapsedGroups = this.props.grid 
      ? getCollapsedGroupLevels(this.props.grid)
      : [];

    return {
      columns: filterColPropertiesToBeSaved(this.props.columnsMapping),
      moduleName: this.props.moduleName,
      frozenColumns: this.props.frozenColumns,
      sorting: this.props.collection && parseSortDescriptions(this.props.collection.sortDescriptions),
      groups: this.props.collection && parseGroupPanels(this.props.collection.groupDescriptions),
      filterDefinition: filterDefinition,
      collapsedGroups: collapsedGroups,
    };
  };

  render() {
    const { isOpen, showManageModal, showSaveAsModal, saving, creating } = this.state;
    const { props } = this;
    const isLoading = saving || creating;
    const buttonDisabled = props.disabled || isLoading;

    // Display name for current view
    const displayViewName = props.currentViewName || 'Default View';
    const isDefaultView = !props.currentViewName;

    // Save button is disabled on Default View
    const saveButtonDisabled = buttonDisabled || isDefaultView;

    // Determine button text
    var saveButtonText = 'Save';
    if (saving) {
      saveButtonText = 'Saving...';
    } else if (props.saveStatus === 'saved') {
      saveButtonText = 'Saved!';
    }

    return (
      <Query
        query={userSavedViewsQuery}
        variables={{ moduleName: props.moduleName }}
        fetchPolicy="cache-and-network"
      >
        {({ data: viewsData, refetch: refetchViews }: any) => {
          // Extract views data with null-safe access
          var savedViewsData = viewsData && viewsData.userSavedViews ? viewsData.userSavedViews : null;
          var views = savedViewsData && savedViewsData.views ? savedViewsData.views : [];
          var maxViews = savedViewsData && savedViewsData.maxViews ? savedViewsData.maxViews : 5;
          var canCreateNew = views.length < maxViews;

          return (
            <Mutation mutation={saveNamedViewMutation}>
              {(saveNamedView: any) => (
                <Mutation mutation={createViewMutation}>
                  {(createView: any) => (
                    <Mutation mutation={setLastUsedViewMutation}>
                      {(setLastUsedView: any) => {
                        // Handler for loading Default View
                        var handleLoadDefaultView = async () => {
                          this.setState({ isOpen: false });
                          
                          // Persist the switch to Default View (viewName = null)
                          try {
                            await setLastUsedView({
                              variables: {
                                moduleName: props.moduleName,
                                viewName: null,
                              },
                            });
                          } catch (e) {
                            console.error('[SaveViewDropdown] Failed to persist Default View switch:', e);
                          }
                          
                          // Apply default view settings to the grid
                          props.onResetToDefault();
                        };

                        // Handler for loading a saved view using cached settings
                        var handleLoadView = async (view) => {
                          this.setState({ isOpen: false });
                          
                          // Persist the view switch
                          try {
                            await setLastUsedView({
                              variables: {
                                moduleName: props.moduleName,
                                viewName: view.viewName,
                              },
                            });
                          } catch (e) {
                            console.error('[SaveViewDropdown] Failed to persist view switch:', e);
                          }
                          
                          // Apply the view settings directly from the cached data
                          props.onApplyViewSettings({
                            columns: view.columns || [],
                            sorting: view.sorting || [],
                            groups: view.groups || [],
                            frozenColumns: view.frozenColumns || 0,
                            filterDefinition: view.filterDefinition || null,
                            collapsedGroups: view.collapsedGroups || [],
                          }, view.viewName);
                        };

                        // Handle primary button click (save to current view)
                        var handleSaveClick = async () => {
                          // Don't save if on Default View
                          if (!props.currentViewName) {
                            return;
                          }

                          this.setState({ saving: true });
                          try {
                            var result = await saveNamedView({
                              variables: {
                                input: {
                                  ...this.getViewSettings(),
                                  viewName: props.currentViewName,
                                },
                              },
                            });

                            if (result.data.saveNamedView.status) {
                              props.onViewSaved('saved', 'saved', props.currentViewName);
                              // Refetch views to update the cached settings
                              refetchViews();
                            } else {
                              props.onViewSaved('error', 'error', props.currentViewName);
                            }
                          } catch (e) {
                            props.onViewSaved('error', 'error', props.currentViewName);
                          }
                          this.setState({ saving: false });
                        };

                        // Handle creating new view
                        var handleCreateView = async (viewName) => {
                          this.setState({ creating: true });
                          try {
                            var result = await createView({
                              variables: {
                                input: {
                                  ...this.getViewSettings(),
                                  viewName: viewName,
                                },
                              },
                            });

                            if (result.data.createView.status) {
                              this.setState({ showSaveAsModal: false });
                              refetchViews();
                              props.onViewSaved('saved', 'saved', viewName);
                              props.onViewLoaded(viewName);
                            } else {
                              alert(result.data.createView.message || 'Failed to create view');
                            }
                          } catch (e) {
                            alert('Failed to create view');
                          }
                          this.setState({ creating: false });
                        };

                        return (
                          <React.Fragment>
                            <DesktopView>
                              <div className="save-view-container">
                                {/* Left side: View selector dropdown */}
                                <div className="save-view-selector" ref={this.dropdownRef}>
                                  <button
                                    className="save-view-selector-btn"
                                    onClick={() => this.setState({ isOpen: !isOpen })}
                                    disabled={props.disabled}
                                    type="button"
                                  >
                                    <span className="save-view-selector-label">View:</span>
                                    <span className="save-view-selector-value">{displayViewName}</span>
                                    <Glyphicon glyph="chevron-down" className="save-view-selector-arrow" />
                                  </button>

                                  {/* Dropdown menu */}
                                  {isOpen && (
                                    <div className="save-view-menu">
                                      <div className="save-view-menu-section-label">Switch View</div>
                                      
                                      {/* Default View - always first */}
                                      <button
                                        className={`save-view-menu-item ${isDefaultView ? 'active' : ''}`}
                                        onClick={handleLoadDefaultView}
                                        type="button"
                                      >
                                        {isDefaultView && (
                                          <Glyphicon glyph="ok" className="save-view-check" />
                                        )}
                                        <span className="save-view-name">Default View</span>
                                      </button>

                                      {/* Saved views list */}
                                      {views.map(function(view) {
                                        return (
                                          <button
                                            key={view.viewName}
                                            className={`save-view-menu-item ${
                                              view.viewName === props.currentViewName ? 'active' : ''
                                            }`}
                                            onClick={function() { handleLoadView(view); }}
                                            type="button"
                                          >
                                            {view.viewName === props.currentViewName && (
                                              <Glyphicon glyph="ok" className="save-view-check" />
                                            )}
                                            <span className="save-view-name">{view.viewName}</span>
                                          </button>
                                        );
                                      })}

                                      {views.length > 0 && (
                                        <div className="save-view-menu-divider" />
                                      )}

                                      {/* Manage Views option */}
                                      <button
                                        className="save-view-menu-item"
                                        onClick={() => {
                                          this.setState({ isOpen: false, showManageModal: true });
                                        }}
                                        disabled={views.length === 0}
                                        type="button"
                                      >
                                        <Glyphicon glyph="cog" />
                                        <span>Manage Views...</span>
                                      </button>
                                    </div>
                                  )}
                                </div>

                                {/* Right side: Save buttons stacked vertically - Save as New on top */}
                                <div className="save-view-buttons">
                                  <ButtonAction
                                    onClick={() => this.setState({ showSaveAsModal: true })}
                                    bsSize="xsmall"
                                    disabled={buttonDisabled || !canCreateNew}
                                    className="save-view-save-as-btn"
                                    title={!canCreateNew ? `Maximum of ${maxViews} views reached` : ''}
                                  >
                                    <Glyphicon glyph="plus" /> Save as New
                                  </ButtonAction>

                                  <ButtonAction
                                    onClick={handleSaveClick}
                                    bsSize="xsmall"
                                    disabled={saveButtonDisabled}
                                    className="save-view-save-btn"
                                    title={isDefaultView ? 'Select a saved view to enable saving' : ''}
                                  >
                                    <Glyphicon glyph="floppy-disk" /> {saveButtonText}
                                  </ButtonAction>
                                </div>
                              </div>
                            </DesktopView>

                            <MobileView>
                              {/* Mobile implementation - simplified button that opens action sheet */}
                              <button
                                onClick={() => this.setState({ isOpen: true })}
                                disabled={buttonDisabled}
                                className="save-view-mobile-btn"
                                type="button"
                              >
                                <img src="assets/save-bottom.svg" alt="save icon" />
                                Views
                              </button>

                              {/* Mobile action sheet */}
                              {isOpen && (
                                <div className="save-view-mobile-overlay" onClick={() => this.setState({ isOpen: false })}>
                                  <div className="save-view-mobile-sheet" onClick={(e) => e.stopPropagation()}>
                                    <div className="save-view-mobile-header">
                                      <span>Views</span>
                                      <button onClick={() => this.setState({ isOpen: false })} type="button">&times;</button>
                                    </div>

                                    {/* Current view indicator for mobile */}
                                    <div className="save-view-mobile-current">
                                      Current: <strong>{displayViewName}</strong>
                                    </div>

                                    {/* Save as New button - always first */}
                                    <button
                                      className="save-view-mobile-item save-view-mobile-action"
                                      onClick={() => {
                                        this.setState({ isOpen: false, showSaveAsModal: true });
                                      }}
                                      disabled={!canCreateNew}
                                      type="button"
                                    >
                                      <Glyphicon glyph="plus" />
                                      Save as New
                                      {!canCreateNew && (
                                        <span className="save-view-limit-note">({maxViews} max)</span>
                                      )}
                                    </button>

                                    {/* Save button - only if on a named view */}
                                    {!isDefaultView && (
                                      <button
                                        className="save-view-mobile-item save-view-mobile-action"
                                        onClick={handleSaveClick}
                                        disabled={buttonDisabled}
                                        type="button"
                                      >
                                        <Glyphicon glyph="floppy-disk" />
                                        Save "{props.currentViewName}"
                                      </button>
                                    )}

                                    <div className="save-view-mobile-divider" />

                                    <div className="save-view-mobile-section-label">Switch View</div>

                                    {/* Default View - always first */}
                                    <button
                                      className={`save-view-mobile-item ${isDefaultView ? 'active' : ''}`}
                                      onClick={handleLoadDefaultView}
                                      type="button"
                                    >
                                      {isDefaultView && <Glyphicon glyph="ok" />}
                                      Default View
                                    </button>

                                    {views.map(function(view) {
                                      return (
                                        <button
                                          key={view.viewName}
                                          className={`save-view-mobile-item ${view.viewName === props.currentViewName ? 'active' : ''}`}
                                          onClick={function() { handleLoadView(view); }}
                                          type="button"
                                        >
                                          {view.viewName === props.currentViewName && <Glyphicon glyph="ok" />}
                                          {view.viewName}
                                        </button>
                                      );
                                    })}

                                    {views.length > 0 && (
                                      <React.Fragment>
                                        <div className="save-view-mobile-divider" />

                                        <button
                                          className="save-view-mobile-item"
                                          onClick={() => {
                                            this.setState({ isOpen: false, showManageModal: true });
                                          }}
                                          type="button"
                                        >
                                          <Glyphicon glyph="cog" />
                                          Manage Views
                                        </button>
                                      </React.Fragment>
                                    )}
                                  </div>
                                </div>
                              )}
                            </MobileView>

                            {/* Modals */}
                            <SaveAsNewViewModal
                              show={showSaveAsModal}
                              onHide={() => this.setState({ showSaveAsModal: false })}
                              onSave={handleCreateView}
                              loading={creating}
                              maxLength={20}
                              existingNames={views.map(function(v) { return v.viewName; })}
                            />

                            <ManageViewsModal
                              show={showManageModal}
                              onHide={() => this.setState({ showManageModal: false })}
                              moduleName={props.moduleName}
                              views={views}
                              maxViews={maxViews}
                              currentViewName={props.currentViewName}
                              onViewsChanged={refetchViews}
                              onViewDeleted={function(deletedName) {
                                if (deletedName === props.currentViewName) {
                                  // Current view was deleted - reset to default
                                  handleLoadDefaultView();
                                }
                              }}
                            />
                          </React.Fragment>
                        );
                      }}
                    </Mutation>
                  )}
                </Mutation>
              )}
            </Mutation>
          );
        }}
      </Query>
    );
  }
}

export default SaveViewDropdown;
